'use client'

import { useEffect, useState } from 'react'
import { InfoCircledIcon } from '@radix-ui/react-icons'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatDateUTC } from '@/lib/utils'

interface ProjectSettings {
  end_date: string | null
  end_date_banner_text: string | null
}

const CACHE_KEY = 'project_settings_cache'
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes
const IS_DEV = process.env.NODE_ENV === 'development'

interface CachedSettings {
  data: ProjectSettings
  timestamp: number
}

const getCachedSettings = (): ProjectSettings | null => {
  if (IS_DEV) return null // No caching in development

  try {
    const cached = localStorage.getItem(CACHE_KEY)
    if (!cached) return null

    const parsed: CachedSettings = JSON.parse(cached)
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY)
      return null
    }
    return parsed.data
  } catch {
    return null
  }
}

const setCachedSettings = (data: ProjectSettings) => {
  if (IS_DEV) return // No caching in development

  try {
    const cached: CachedSettings = { data, timestamp: Date.now() }
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached))
  } catch {
    // Ignore storage errors
  }
}

export const ProjectEndBanner = () => {
  const [mounted, setMounted] = useState(false)
  const [settings, setSettings] = useState<ProjectSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [currentTime, setCurrentTime] = useState(new Date())

  useEffect(() => {
    setMounted(true)

    // Check cache first
    const cached = getCachedSettings()
    if (cached) {
      setSettings(cached)
      setLoading(false)
      return
    }

    // Fetch project settings from API (public endpoint - no auth required)
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/project-settings')
        if (response.ok) {
          const result = await response.json()
          setSettings(result.data)
          setCachedSettings(result.data)
        }
      } catch (error) {
        console.error('Failed to fetch project settings:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchSettings()
  }, [])

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [])

  // Prevent hydration mismatch
  if (!mounted || loading) return null

  // No end date set - don't show banner
  if (!settings?.end_date) {
    return null
  }

  const effectiveEndDate = new Date(settings.end_date)

  // Display end date as 1 second before midnight (23:59:59)
  const displayEndDate = new Date(effectiveEndDate.getTime() - 1000)

  const endDateFormatted = formatDateUTC(displayEndDate)
  const currentTimeFormatted = formatDateUTC(currentTime)

  // Calculate time remaining
  const timeRemainingMs = effectiveEndDate.getTime() - currentTime.getTime()
  const days = Math.floor(timeRemainingMs / (24 * 60 * 60 * 1000))
  const hours = Math.floor((timeRemainingMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
  const minutes = Math.floor((timeRemainingMs % (60 * 60 * 1000)) / (60 * 1000))

  const countdown = timeRemainingMs > 0
    ? `${days}d ${hours}h ${minutes}m`
    : 'Ended'

  const isEnded = timeRemainingMs <= 0

  // Project has ended
  if (isEnded) {
    const displayText = `This project ended on ${endDateFormatted}. (Current time: ${currentTimeFormatted})`
    const endedInfoText = `Mining rewards are no longer being earned.  Offboarding will appear below when ready.  Thank you for participating.`

    return (
      <div className="bg-red-600 text-white p-3 text-center">
        <p className="font-semibold text-sm inline-flex items-center justify-center gap-2">
          {displayText}
          <Popover>
            <PopoverTrigger>
              <InfoCircledIcon className="h-4 w-4 cursor-pointer" />
            </PopoverTrigger>
            <PopoverContent className="text-sm">
              <p>{endedInfoText}</p>
            </PopoverContent>
          </Popover>
        </p>
      </div>
    )
  }

  const customText = settings?.end_date_banner_text
  const displayText = `This project ends on ${endDateFormatted} UTC.  Time remaining: ${countdown}.  Current UTC time: ${currentTimeFormatted}`
  const endingInfoText = customText || ``

  return (
    <div className="bg-yellow-600 text-white p-3 text-center">
      <p className="font-semibold text-sm inline-flex items-center justify-center gap-2">
        {displayText}
        <Popover>
          <PopoverTrigger>
            <InfoCircledIcon className="h-4 w-4 cursor-pointer" />
          </PopoverTrigger>
          <PopoverContent className="text-sm">
            <p>{endingInfoText}</p>
          </PopoverContent>
        </Popover>
      </p>
    </div>
  )
}
